import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';

import { Check, ChevronsUpDown } from 'lucide-react'; // Optional: Search icon (if using an icon library)
import { cn } from '../../utils';
import { Button, ButtonProps } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { useVisualViewportHeight } from '../hooks/useVisualViewportHeight';

export const SelectListFilter = ({
  items,
  disabledItems,
  disabled,
  defaultValue,
  placeholder,
  variant,
  triggerClassName,
  handleChange,
}: {
  items?: { value: string; label: string }[];
  disabledItems?: { value: string }[];
  disabled?: boolean;
  defaultValue?: string;
  placeholder?: string;
  variant?: ButtonProps['variant'];
  triggerClassName?: string;
  handleChange?: (item: { value: string; label: string }) => void;
}) => {
  const refSelected = createRef<HTMLDivElement>();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(defaultValue);

  const visualViewport = useVisualViewportHeight();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const listMarginBottom = useMemo(() => {
    if (visualViewport && triggerRef.current) {
      const offset =
        triggerRef.current?.getBoundingClientRect().top - visualViewport;
      return offset > 0 ? offset : 0;
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualViewport]);

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    // Scroll the selected element into view.
    if (open && selected) {
      setTimeout(() => {
        // Refs aren't state objects so we can't identify their creation.
        // Since the open state change triggers a render, the ref is set
        // after the render, so we need to skip the current event loop
        // in order to access it.
        refSelected.current?.scrollIntoView();
      });
    }
  }, [open, selected, refSelected]);

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger
          asChild
          disabled={disabled || typeof items === 'undefined'}
          ref={triggerRef}
        >
          <Button
            variant={variant || 'outline'}
            role="combobox"
            aria-expanded={open}
            className={`w-full justify-between ${triggerClassName}`}
          >
            <span className="w-[80%] text-start overflow-clip text-ellipsis">
              {selected
                ? items?.find((item) => item.value === selected)?.label
                : placeholder ?? 'Search'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // Fix as per https://github.com/shadcn-ui/ui/issues/4656
          className="w-[--radix-popover-trigger-width] p-2"
          style={{ marginBottom: listMarginBottom }}
        >
          <Command
            filter={(val, search) => {
              return val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
            }}
          >
            <CommandInput placeholder={placeholder} />
            <CommandEmpty>No items</CommandEmpty>
            <CommandGroup className="max-h-32 h-32 sm:h-60 sm:max-h-60 overflow-y-auto">
              {items?.map((item) => {
                const isItemDisabled = !!disabledItems?.find(
                  (dItem) => dItem.value === item.value
                );
                return (
                  <CommandItem
                    disabled={isItemDisabled}
                    className={isItemDisabled ? 'cursor-not-allowed' : ''}
                    ref={selected === item.value ? refSelected : undefined}
                    key={item.value}
                    value={item.label}
                    onSelect={(currentValue) => {
                      const value = items.find(
                        (item) => item.label.toLowerCase() === currentValue
                      )?.value;
                      setSelected(value ?? '');

                      if (handleChange) {
                        handleChange({ value: item.value, label: item.label });
                      }

                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selected === item.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {item.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SelectListFilter;
